import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Blog, BlogService } from '../services/blog.service';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from '@geekeno/ngx-website/services';
import { BlogListComponent } from '../blog-list/blog-list.component';
import { rxResource } from '@angular/core/rxjs-interop';
import { map, of } from 'rxjs';

@Component({
  selector: 'app-blog-detail',
  imports: [RouterModule, BlogListComponent, DatePipe],
  templateUrl: './blog-detail.component.html',
  styleUrl: './blog-detail.component.scss',
})
export class BlogDetailComponent {
  private route = inject(ActivatedRoute);
  private blogService = inject(BlogService);
  private seoService = inject(SeoService);

  // Convert route.data observable to signal
  private routeData = toSignal(this.route.data, {
    initialValue: {} as { blog: Blog },
  });

  // Extract blog from route data using computed
  blog = computed(() => this.routeData().blog);

  // Use rxResource to fetch related blogs reactively
  private relatedBlogsResource = rxResource<Blog[], Blog | undefined>({
    params: () => this.blog(),
    stream: ({ params: blog }) => {
      // Guard against undefined during initial hydration
      if (!blog) {
        return of([]);
      }
      if (blog.related_posts?.length) {
        // If blog has related_posts, return them as an observable
        return of(blog.related_posts);
      }
      // Otherwise fetch all blogs and pick random ones
      return this.blogService.getBlogs(1, 100).pipe(
        map((data) => {
          const otherBlogs = data.results.filter((b) => b.slug !== blog.slug);
          return this.getRandomBlogs(otherBlogs, 3);
        }),
      );
    },
  });

  // Expose related blogs as a signal
  relatedBlogs = computed(() => this.relatedBlogsResource.value() ?? []);

  constructor() {
    // Update SEO whenever blog changes
    effect(() => {
      const blog = this.blog();
      if (blog) {
        this.seoService.setSEO({
          title: blog.title,
          description: blog.snippet,
          image: blog.cover_image,
          ogType: 'article',
          articleAuthor: `${blog.author.first_name} ${blog.author.last_name}`,
          articlePublishedTime: blog.timestamp,
        });
      }
    });
  }

  private getRandomBlogs(blogs: Blog[], count: number): Blog[] {
    const shuffled = blogs.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }
}
